/* eslint-disable lines-between-class-members */

import * as THREE from "three";
const { Vector2 } = THREE;
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const ctor = typeof globalThis !== "undefined" && (globalThis.TroikaTextNode || globalThis.TextNode);
const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

const TEXT_PROPS = [
  "text",
  "anchorX",
  "anchorY",
  "color",
  "curveRadius",
  "depthOffset",
  "direction",
  "fillOpacity",
  "fontSize",
  "textAlign",
  "letterSpacing",
  "clipRectMin",
  "clipRectMax",
  "lineHeight",
  "overflowWrap",
  "strokeColor",
  "strokeOpacity",
  "strokeWidth",
  "whiteSpace",
  "maxWidth",
  "textIndent",
  "opacity",
  "side",
  "outlineBlur",
  "outlineColor",
  "outlineOffsetX",
  "outlineOffsetY",
  "outlineOpacity",
  "outlineWidth"
];

class TextAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;
    const COMPONENT = "text";
    const pending = (this._pendingUpdates = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      if (pending[COMPONENT]) {
        const current = { ...(el.getAttribute(COMPONENT) || {}) };
        Object.assign(current, pending[COMPONENT]);
        el.setAttribute(COMPONENT, current);
        delete pending[COMPONENT];
      }
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      const name = e?.detail?.name;
      if (name === COMPONENT) flush();
    });

    const setTextProp = (k, v) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(COMPONENT);
      if (!attr) {
        (pending[COMPONENT] ||= {})[k] = v;
      } else {
        const next = { ...attr, [k]: v };
        el.setAttribute(COMPONENT, next);
      }
    };

    const getTextProp = k => {
      const el = this.sourceObject?.el;
      if (!el) return pending[COMPONENT]?.[k];
      const attr = el.getAttribute(COMPONENT);
      return (attr && attr[k]) ?? pending[COMPONENT]?.[k];
    };

    // Map text properties to A-Frame text component attributes
    const propMap = {
      text: "value",
      textAlign: "textAlign",
      anchorX: "anchorX", 
      anchorY: "anchorY",
      color: "color",
      curveRadius: "curveRadius",
      depthOffset: "depthOffset",
      direction: "direction",
      fillOpacity: "fillOpacity",
      fontSize: "fontSize",
      letterSpacing: "letterSpacing",
      lineHeight: "lineHeight",
      maxWidth: "maxWidth",
      overflowWrap: "overflowWrap",
      strokeColor: "strokeColor",
      strokeOpacity: "strokeOpacity",
      strokeWidth: "strokeWidth",
      textIndent: "textIndent",
      whiteSpace: "whiteSpace",
      opacity: "opacity",
      side: "side",
      outlineBlur: "outlineBlur",
      outlineColor: "outlineColor",
      outlineOffsetX: "outlineOffsetX",
      outlineOffsetY: "outlineOffsetY",
      outlineOpacity: "outlineOpacity",
      outlineWidth: "outlineWidth"
    };

    // Bind standard properties
    Object.keys(propMap).forEach(prop => {
      const attr = propMap[prop];
      Object.defineProperty(this, prop, {
        get: () => getTextProp(attr),
        set: v => setTextProp(attr, v)
      });
    });

    // Handle clipRect as a special case (array of 4 numbers)
    Object.defineProperty(this, "clipRect", {
      get: () => {
        const val = getTextProp("clipRect");
        if (typeof val === "string") {
          const nums = val.split(/[\s,]+/).map(parseFloat).filter(n => !isNaN(n));
          return nums.length === 4 ? nums : null;
        }
        return val;
      },
      set: v => {
        const formatted = Array.isArray(v) ? v.join(" ") : v;
        setTextProp("clipRect", formatted);
      }
    });

    Object.defineProperty(this, "clipRectMin", {
      get: () => {
        const val = this.clipRect;
        return val ? new Vector2(val[0], val[1]) : undefined;
      },
      set: min => {
        const current = this.clipRect || [0, 0, 0, 0];
        const newArr = [min.x || min[0] || 0, min.y || min[1] || 0, current[2], current[3]];
        this.clipRect = newArr;
      }
    });

    Object.defineProperty(this, "clipRectMax", {
      get: () => {
        const val = this.clipRect;
        return val ? new Vector2(val[2], val[3]) : undefined;
      },
      set: max => {
        const current = this.clipRect || [0, 0, 0, 0];
        const newArr = [current[0], current[1], max.x || max[0] || 0, max.y || max[1] || 0];
        this.clipRect = newArr;
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

class TextEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    TEXT_PROPS.forEach(prop => {
      const isVec2Prop = prop === "clipRectMin" || prop === "clipRectMax";
      const isColorProp = prop === "color" || prop === "strokeColor" || prop === "outlineColor";

      if (isVec2Prop) {
        Object.defineProperty(this, prop, {
          get: () => {
            const val = this._values.get(prop);
            // Always return a Vector2, defaulting to (0,0) if undefined
            return val instanceof Vector2 ? val : new Vector2(0, 0);
          },
          set: v => {
            let finalValue;
            if (v !== undefined && v !== null) {
              if (Array.isArray(v) && v.length >= 2) {
                finalValue = new Vector2(v[0], v[1]);
              } else if (v && typeof v.x === "number" && typeof v.y === "number") {
                finalValue = new Vector2(v.x, v.y);
              } else if (v instanceof Vector2) {
                finalValue = v.clone();
              } else {
                finalValue = new Vector2(0, 0); // fallback
              }
            } else {
              finalValue = new Vector2(0, 0); // fallback for null/undefined
            }
            this._values.set(prop, finalValue);
            const desc = this._originalDescriptors.get(prop);
            if (desc?.set) {
              try {
                desc.set.call(this.sourceObject, finalValue);
              } catch {
                /* ignore */
              }
            } else {
              try {
                // Avoid recursive setter: if the property already holds a Vector2 we mutate it in place
                const cur = this.sourceObject[prop];
                if (cur && cur.isVector2) {
                  cur.copy(finalValue);
                } else {
                  // fallback assign when no existing Vector2 (first time)
                  Object.defineProperty(this.sourceObject, prop, { value: finalValue, writable: true, configurable: true });
                }

                this.sourceObject.onChange();
              } catch {
                /* ignore */
              }
            }
          }
        });
        return;
      }

      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          let valueToSet = v;
          
          // For color properties, ensure we always set a THREE.Color on the underlying node
          if (isColorProp) {
            if (v && !v.isColor) {
              // Convert string/number colors to THREE.Color
              valueToSet = new THREE.Color(v);
            }
          }
          
          this._values.set(prop, v); // Store original value in adapter
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(this.sourceObject, valueToSet);
            } catch {
              /* ignore */
            }
          } else if (isColorProp && this.sourceObject?.[prop]?.set) {
            this.sourceObject[prop].set(valueToSet);
            this._values.set(prop, this.sourceObject[prop]);
          } else {
            try {
              this.sourceObject[prop] = valueToSet;
              this.sourceObject.onChange();
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed from source without invoking setters when possible
      const desc = originalDescriptors.get(prop);
      let initial;
      try {
        if (desc?.get) initial = desc.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      
      // Ensure Vector2 props have valid defaults
      if (isVec2Prop && (!initial || !(initial instanceof Vector2))) {
        initial = new Vector2(0, 0);
      }
      
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of TEXT_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  // A-Frame path if we have an element
  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new TextAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  // Editor / plain Three path
  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", {
    value: new TextEditorAdapter(self, descriptors),
    configurable: true
  });
  return self._adapter;
}

export class TextElement extends ScriptableObject {
  static scriptableType = "text";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      el.setAttribute("text", {
        value: initial.text || "",
        color: initial.color || "#FFF",
        fontSize: initial.fontSize || 1,
        anchorX: initial.anchorX || "center",
        anchorY: initial.anchorY || "center",
        textAlign: initial.textAlign || "center"
      });

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real TroikaTextNode and upgrade to scriptable
    const node = createTextElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    console.log("Copying text element", source, this);

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs - following old implementation pattern
      if (val?.isColor) {
        this[prop] = val.clone();
      } else if ((prop === "clipRectMin" || prop === "clipRectMax") && val instanceof Vector2) {
        this[prop] = val.clone();
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else {
        this[prop] = val;
      }
    };

    for (const p of TEXT_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of TEXT_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as old implementation - only clone if it's actually a cloneable object
      if (v?.isColor) {
        init[p] = v.clone();
      } else if ((p === "clipRectMin" || p === "clipRectMax") && v instanceof Vector2) {
        init[p] = v.clone();
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else {
        init[p] = v;
      }
    }

    const dest = new TextElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade an editor TroikaTextNode to scriptable and attach adapter + proxies
function makeEditorTextNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || ctor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: TextElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = TextElement.scriptableType;
  }

  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new TextEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new TextEditorAdapter(node, descriptors);
  }

  for (const prop of TEXT_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* best-effort */
    }
  }

  // Ensure TextElement's enhanced `clone` logic is used for
  // editor-created text nodes. Without this, the instance would fall back to
  // TroikaTextNode (or Object3D) defaults which do not propagate the
  // scriptable properties managed by the adapter.
  try {
    if (node.clone !== TextElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: TextElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }
  } catch {
    node.clone = TextElement.prototype.clone;
  }

  return node;
}

// Editor factory for Text (editor/plain Three)
function createTextElementEditor(initial = {}) {
  const Ctor = globalThis.TroikaTextNode || globalThis.TextNode;
  if (typeof Ctor !== "function") {
    throw new Error("TextElementEditor: TroikaTextNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new Ctor(editor);
  } catch (e) {
    throw new Error(`TextElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorTextNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) {
      // Handle special cases for initial values
      if (k === "color" || k === "strokeColor" || k === "outlineColor") {
        node[k] = new THREE.Color(initial[k]);
      } else {
        node[k] = initial[k];
      }
    }
  }
  
  // Call onChange to ensure the helper is updated with all initial properties
  if (node.onChange) {
    node.onChange();
  }
  
  return node;
}

// If you create objects via adopt(el) (constructor doesn't run),
// the accessor getter calls ensureAdapter(this) and mounts the adapter on first access.
// That's why binding these getters on the prototype is crucial—even for adopted instances.
TEXT_PROPS.forEach(prop => {
  Object.defineProperty(TextElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

ScriptableObjects.registerType("text", source => {
  if (!source) return source;

  // Only promote if type matches (brand can live on obj or on el.object3D)
  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;
  if (type !== TextElement.scriptableType) return source;

  // If given an A-Frame element or a Scriptable with .el -> adopt that element
  if (isAFrameEntity(source)) return TextElement.adopt(source);

  // Editor/plain-Three: upgrade in-place to scriptable and attach adapter + proxies
  if (isObject3D(source)) return makeEditorTextNodeScriptable(source);

  return source;
});

// Named exports expected by external consumers -----------------------------
export { TextAFrameAdapter, TextEditorAdapter, TEXT_PROPS };
